import React from 'react'
import { createUltimatePagination, ITEM_TYPES } from 'react-ultimate-pagination'

// Define a modern color palette
const colors = {
	primary: '#3B82F6', // Blue primary color
	hover: '#2563EB', // Darker blue for hover
	disabled: '#E5E7EB', // Light gray for disabled state
	disabledText: '#9CA3AF', // Gray text for disabled state
	border: '#D1D5DB', // Border color
	text: '#1F2937', // Text color
	activeText: '#FFFFFF', // White text for active state
	background: '#FFFFFF', // White background
}

// Common button styles to maintain consistency
const buttonBase = {
	borderRadius: '0.375rem', // Rounded corners
	fontFamily: 'Inter, system-ui, sans-serif',
	fontSize: '0.875rem',
	padding: '0.5rem 0.75rem',
	margin: '0 0.25rem',
	border: `1px solid ${colors.border}`,
	transition: 'all 0.2s ease',
	cursor: 'pointer',
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
	minWidth: '2.5rem',
	boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
}

const Page = ({ value, isActive, onClick, isDisabled }) => (
	<button
		style={{
			...buttonBase,
			backgroundColor: isActive ? colors.primary : colors.background,
			color: isActive ? colors.activeText : colors.text,
			borderColor: isActive ? colors.primary : colors.border,
			fontWeight: isActive ? '600' : '400',
			opacity: isDisabled ? 0.6 : 1,
			cursor: isDisabled ? 'not-allowed' : 'pointer',
			':hover': {
				backgroundColor: isActive ? colors.hover : colors.background,
				borderColor: isActive ? colors.hover : colors.primary,
				color: isActive ? colors.activeText : colors.primary,
			},
		}}
		onClick={onClick}
		disabled={isDisabled}
	>
		{value}
	</button>
)

const Ellipsis = ({ onClick, isDisabled }) => (
	<button
		style={{
			...buttonBase,
			backgroundColor: colors.background,
			color: colors.text,
			cursor: isDisabled ? 'not-allowed' : 'pointer',
			opacity: isDisabled ? 0.6 : 1,
		}}
		onClick={onClick}
		disabled={isDisabled}
	>
		•••
	</button>
)

// Navigation button styles (first, prev, next, last)
const navButtonStyle = (isDisabled) => ({
	...buttonBase,
	backgroundColor: colors.background,
	color: isDisabled ? colors.disabledText : colors.text,
	borderColor: colors.border,
	opacity: isDisabled ? 0.6 : 1,
	cursor: isDisabled ? 'not-allowed' : 'pointer',
	':hover': {
		backgroundColor: isDisabled ? colors.background : colors.primary,
		color: isDisabled ? colors.disabledText : colors.activeText,
		borderColor: isDisabled ? colors.border : colors.primary,
	},
})

const FirstPageLink = ({ onClick, isDisabled }) => (
	<button
		style={navButtonStyle(isDisabled)}
		onClick={onClick}
		disabled={isDisabled}
		aria-label='First Page'
	>
		<svg
			xmlns='http://www.w3.org/2000/svg'
			width='16'
			height='16'
			viewBox='0 0 24 24'
			fill='none'
			stroke='currentColor'
			strokeWidth='2'
			strokeLinecap='round'
			strokeLinejoin='round'
		>
			<polyline points='11 17 6 12 11 7'></polyline>
			<polyline points='18 17 13 12 18 7'></polyline>
		</svg>
	</button>
)

const PreviousPageLink = ({ onClick, isDisabled }) => (
	<button
		style={navButtonStyle(isDisabled)}
		onClick={onClick}
		disabled={isDisabled}
		aria-label='Previous Page'
	>
		<svg
			xmlns='http://www.w3.org/2000/svg'
			width='16'
			height='16'
			viewBox='0 0 24 24'
			fill='none'
			stroke='currentColor'
			strokeWidth='2'
			strokeLinecap='round'
			strokeLinejoin='round'
		>
			<polyline points='15 18 9 12 15 6'></polyline>
		</svg>
	</button>
)

const NextPageLink = ({ onClick, isDisabled }) => (
	<button
		style={navButtonStyle(isDisabled)}
		onClick={onClick}
		disabled={isDisabled}
		aria-label='Next Page'
	>
		<svg
			xmlns='http://www.w3.org/2000/svg'
			width='16'
			height='16'
			viewBox='0 0 24 24'
			fill='none'
			stroke='currentColor'
			strokeWidth='2'
			strokeLinecap='round'
			strokeLinejoin='round'
		>
			<polyline points='9 18 15 12 9 6'></polyline>
		</svg>
	</button>
)

const LastPageLink = ({ onClick, isDisabled }) => (
	<button
		style={navButtonStyle(isDisabled)}
		onClick={onClick}
		disabled={isDisabled}
		aria-label='Last Page'
	>
		<svg
			xmlns='http://www.w3.org/2000/svg'
			width='16'
			height='16'
			viewBox='0 0 24 24'
			fill='none'
			stroke='currentColor'
			strokeWidth='2'
			strokeLinecap='round'
			strokeLinejoin='round'
		>
			<polyline points='13 17 18 12 13 7'></polyline>
			<polyline points='6 17 11 12 6 7'></polyline>
		</svg>
	</button>
)

const itemTypeToComponent = {
	[ITEM_TYPES.PAGE]: Page,
	[ITEM_TYPES.ELLIPSIS]: Ellipsis,
	[ITEM_TYPES.FIRST_PAGE_LINK]: FirstPageLink,
	[ITEM_TYPES.PREVIOUS_PAGE_LINK]: PreviousPageLink,
	[ITEM_TYPES.NEXT_PAGE_LINK]: NextPageLink,
	[ITEM_TYPES.LAST_PAGE_LINK]: LastPageLink,
}

// Create a container component to wrap the pagination with proper spacing
const PaginationContainer = ({ children }) => (
	<div
		style={{
			display: 'flex',
			justifyContent: 'center',
			alignItems: 'center',
			margin: '1.5rem 0',
			gap: '0.25rem',
		}}
	>
		{children}
	</div>
)

const UltimatePaginationBasic = (props) => {
	const PaginationComponent = createUltimatePagination({
		itemTypeToComponent,
	})

	return (
		<PaginationContainer>
			<PaginationComponent {...props} />
		</PaginationContainer>
	)
}

export default UltimatePaginationBasic
